import React, { Component, TouchEvent } from 'react';
import Style from './slide-swiper.less';
import classnames from 'classnames';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import ScaleSlide from '@src/framework/editor/src/page/index/components/canvas/scale-slide';
import { RenderPostionType } from '@src/js/constant';

export interface SwiperProps {
    slideArray: ISlideModel[];
    slideWidth: number;
    slideHeight: number;
    contentSize: {
        width: number;
        height: number;
    };
    currentIndex: number;
    onChangeIndex?: (index: number) => void;
}

export class SlideSwiper extends Component<SwiperProps> {
    private length: number;
    private startX: number;
    private endX: number;

    public constructor(props: SwiperProps) {
        super(props);
        this.length = props.slideArray.length;
        this.startX = 0;
        this.endX = 0;
    }

    public render() {
        const { currentIndex } = this.props;
        const prevIndex = currentIndex === 0 ? this.length - 1 : currentIndex - 1;
        const nextIndex = currentIndex === this.length - 1 ? 0 : currentIndex + 1;

        const { slideArray } = this.props;
        const { contentSize } = this.props;
        const totalLength = slideArray.length;
        let noRight = false;
        let noLeft = false;
        if (currentIndex === 0) {
            noLeft = true;
        }
        if (currentIndex === totalLength - 1) {
            noRight = true;
        }

        const { slideWidth } = this.props;
        const { slideHeight } = this.props;
        const scale = this.getScale(contentSize.width - 2, contentSize.height - 2);
        return (
            <div className={Style['swiper-container']}>
                <div
                    className={Style['swiper-wrapper']}
                    onTouchStart={this.handleTouchStart}
                    onTouchMove={this.handleTouchMove}
                    onTouchEnd={this.handleTouchEnd}
                    style={this.getContentWrapperLayoutStyle(noLeft, noRight)}
                >
                    {
                        !noLeft
                        && <div
                            className={classnames({
                                [Style['swiper-slide']]: true,
                                [Style['prev-slide']]: true,
                            })}
                            style={{
                                width: `${contentSize.width}px`,
                                marginLeft: `${-contentSize.width + (noRight ? 28 : 16)}px`,
                            }}
                            key={prevIndex}
                        >
                            <ScaleSlide
                                width={contentSize.width - 2}
                                height={contentSize.height - 2}
                                isShown={true}
                                slideModel={slideArray[prevIndex]}
                                buildRenderTreeAsync={false}
                                shouldUpdate={true}
                                fromScale={0}
                                region={RenderPostionType.PAGINGSELECTION}
                                isTemplate={false}
                                styles={{ scaleSlideStyle: { width: `${slideWidth * scale}px`, height: `${slideHeight * scale}px` } }}
                            />
                            <div/>
                        </div>
                    }
                    <div
                        className={classnames({
                            [Style['swiper-slide']]: true,
                            [Style['active-slide']]: true,
                        })}
                        style={{ width: `${contentSize.width}px` }}
                        key={currentIndex}
                    >
                        <ScaleSlide
                            width={contentSize.width - 2}
                            height={contentSize.height - 2}
                            isShown={true}
                            slideModel={slideArray[currentIndex]}
                            buildRenderTreeAsync={false}
                            shouldUpdate={true}
                            fromScale={0}
                            region={RenderPostionType.PAGINGSELECTION}
                            isTemplate={false}
                            styles={{ scaleSlideStyle: { width: `${slideWidth * scale}px`, height: `${slideHeight * scale}px` } }}
                        />
                        <div/>
                    </div>
                    {
                        !noRight
                        && <div
                            className={classnames({
                                [Style['swiper-slide']]: true,
                                [Style['next-slide']]: true,
                            })}
                            style={{ width: `${contentSize.width}px` }}
                            key={nextIndex}
                        >
                            <ScaleSlide
                                width={contentSize.width - 2}
                                height={contentSize.height - 2}
                                isShown={true}
                                slideModel={slideArray[nextIndex]}
                                buildRenderTreeAsync={false}
                                shouldUpdate={true}
                                fromScale={0}
                                region={RenderPostionType.PAGINGSELECTION}
                                isTemplate={false}
                                styles={{ scaleSlideStyle: { width: `${slideWidth * scale}px`, height: `${slideHeight * scale}px` } }}
                            />
                            <div/>
                        </div>
                    }
                </div>
                <div
                    className={Style['swiper-wrapper-overlay']}
                    onTouchStart={this.handleTouchStart}
                    onTouchMove={this.handleTouchMove}
                    onTouchEnd={this.handleTouchEnd}
                    style={this.getContentWrapperLayoutStyle(noLeft, noRight)}
                >
                    {
                        !noLeft
                        && <div className={Style['swiper-wrapper-overlay-left']}/>
                    }
                    <div />
                    {
                        !noRight
                        && <div className={Style['swiper-wrapper-overlay-right']}/>
                    }
                </div>
            </div>
        );
    }

    private getGridTemplateColumns(noLeft: boolean, noRight: boolean) {
        const contentWidth = this.props.contentSize.width;
        if (noLeft && noRight) {
            return '100%';
        }
        if (noRight) {
            return `28px ${contentWidth}px`;
        }
        if (noLeft) {
            return `${contentWidth}px 28px`;
        }
        return `16px ${contentWidth}px 16px`;
    }

    private getContentWrapperLayoutStyle(noLeft: boolean, noRight: boolean) {
        const gridTemplateColumns = this.getGridTemplateColumns(noLeft, noRight);
        if (noLeft && noRight) {
            return { gridTemplateColumns, margin: '0 28px' };
        }
        if (noRight) {
            return { gridTemplateColumns, marginRight: '16px' };
        }
        if (noLeft) {
            return { gridTemplateColumns, marginLeft: '16px' };
        }
        return { gridTemplateColumns };
    }

    private nextSlide = () => {
        if (this.props.currentIndex + 1 < this.length) {
            this.props.onChangeIndex?.(this.props.currentIndex + 1);
        }
    };

    private prevSlide = () => {
        if (this.props.currentIndex - 1 >= 0) {
            this.props.onChangeIndex?.(this.props.currentIndex - 1);
        }
    };

    private handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
        this.startX = e.touches[0].clientX;
        this.endX = e.touches[0].clientX;
    };

    private handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
        this.endX = e.touches[0].clientX;
    };

    private handleTouchEnd = () => {
        if (this.startX - this.endX > 30) {
            this.nextSlide();
        } else if (this.endX - this.startX > 30) {
            this.prevSlide();
        }
    };

    private getScale(width: number, height: number) {
        const { slideWidth } = this.props;
        const { slideHeight } = this.props;
        return width / slideWidth < height / slideHeight
            ? width / slideWidth : height / slideHeight;
    }
}
